import React, { useState, useEffect, useCallback } from 'react';
import { Provider } from 'react-redux';
import store from '../../../state';
import ContextMenu from './ContextMenu';
import { isEqual } from 'lodash';

// this is the version of the context menu that is linked to the table schedule
// component. When right clicking on an interval, the context menu displays.
// When a change is made, the relevant parts of the table schedule are
// refreshed.
const StandAlone = ({ groupId }) => {
  // use one state variable to avoid multiple updates of the underlying
  // components
  const [intervalData, setIntervalData] = useState({
    startTime: null,
    endTime: null,
    ownerId: null,
    ownerType: null
  });

  // array of objects with: owner ID, owner Type, start time to identify the
  // selected intervals
  const [selectedIntervals, setSelectedIntervals] = useState([]);

  const [xCoord, setXCoord] = useState(null);
  const [yCoord, setYCoord] = useState(null);
  const [show, setShow] = useState(false);

  const createIntervalIdentifier = target => ({
    ownerId: target.dataset.ownerId,
    ownerType: target.dataset.ownerType,
    startTime: target.dataset.startTime,
    endTime: target.dataset.endTime
  });

  const displayContextMenu = useCallback(
    event => {
      // but only care about those that right click on editable intervals
      const target = event.target.closest(".schedule-interval.availability-editable[data-owner-type='membership']");

      if (!target) return;

      event.preventDefault();
      setXCoord(event.pageX);
      setYCoord(event.pageY);

      const intervalIdentifier = createIntervalIdentifier(target);
      const existingInterval = selectedIntervals.find(i => isEqual(i, intervalIdentifier));

      if (!existingInterval) {
        // if the right clicked interval is not in the selected intervals, select only that one
        setSelectedIntervals([intervalIdentifier]);
        // remove selected from all intervals
        for (const el of document.querySelectorAll('.schedule-interval.availability-editable.selected')) {
          el.classList.remove('selected');
        }
        // add selected to the right clicked interval
        target.classList.add('selected');
      }

      const data = {
        ...target.dataset,
        assignedFunctionIds: target.dataset.assignedFunctionIds ? target.dataset.assignedFunctionIds.split(',') : []
      };

      setIntervalData(data);
      setShow(true);
    },
    [setXCoord, setYCoord, setIntervalData, setShow, selectedIntervals, setSelectedIntervals]
  );

  const clearSelection = useCallback(() => {
    setSelectedIntervals([]);
    for (const el of document.querySelectorAll('.schedule-interval.availability-editable.selected')) {
      el.classList.remove('selected');
    }
  }, [setSelectedIntervals]);

  const updateSelection = useCallback(
    event => {
      if (!event.ctrlKey && !event.metaKey) return;
      const target = event.target.closest(".schedule-interval.availability-editable[data-owner-type='membership']");

      if (!target) return;

      const intervalIdentifier = createIntervalIdentifier(target);
      const existingInterval = selectedIntervals.find(i => isEqual(i, intervalIdentifier));

      if (existingInterval) {
        setSelectedIntervals(prev => prev.filter(x => x !== existingInterval));
        target.classList.remove('selected');
      } else {
        setSelectedIntervals(prev => [...prev, intervalIdentifier]);
        target.classList.add('selected');
      }

      event.preventDefault();
      event.stopPropagation();
    },
    [selectedIntervals, setSelectedIntervals]
  );

  const hideContextMenu = useCallback(() => {
    setShow(false);
  }, [setShow]);

  useEffect(() => {
    document.addEventListener('click', hideContextMenu);
    document.addEventListener('scroll', hideContextMenu);

    // attach an event listerener to the document. When clicked on a
    // .schedule-interval.availability-editable, display the context menu
    document.addEventListener('contextmenu', displayContextMenu);
    document.addEventListener('click', updateSelection);

    return () => {
      document.removeEventListener('click', hideContextMenu);
      document.removeEventListener('click', updateSelection);
      document.removeEventListener('scroll', hideContextMenu);
      document.removeEventListener('contextmenu', displayContextMenu);
    };
  }, [displayContextMenu, hideContextMenu, updateSelection, selectedIntervals]);

  // notify the tableScheduleManager that changes have happened during the
  // indicated period
  const notifyChanged = (st, et) => {
    setShow(false);
    st = st || minSelectedStartTime();
    et = et || maxSelectedEndTime();
    globalThis.tableScheduleManager.reloadPeriod(st.fromISOString(), et.fromISOString());
    clearSelection();
  };

  const setLoading = () => {
    globalThis.tableScheduleManager.setLoading(
      minSelectedStartTime().fromISOString(),
      maxSelectedEndTime().fromISOString()
    );
  };

  const minSelectedStartTime = useCallback(
    () => selectedIntervals.map(i => i.startTime).sort()[0],
    [selectedIntervals]
  );

  const maxSelectedEndTime = useCallback(
    () =>
      selectedIntervals
        .map(i => i.endTime)
        .sort()
        .reverse()[0],
    [selectedIntervals]
  );

  return (
    <Provider store={store}>
      <ContextMenu
        groupId={groupId}
        xCoord={xCoord}
        yCoord={yCoord}
        startTime={intervalData.startTime}
        endTime={intervalData.endTime}
        ownerId={intervalData.ownerId}
        ownerType={intervalData.ownerType}
        assignedFunctionIds={intervalData.assignedFunctionIds}
        notifyChanged={notifyChanged}
        selectedIntervals={selectedIntervals}
        show={show}
        setLoading={setLoading}
      />
    </Provider>
  );
};

export default StandAlone;
